import * as React from "react";

import { Sheet, SheetBody, SheetContent, SheetHeader, Skeleton } from "Components/ui";
import AccountSection from "./Components/AccountSection";
import PasswordSection from "./Components/PasswordSection";
import WhatsAppSection from "./Components/WhatsAppSection";
import { useProfileData } from "./data";

interface ProfileSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * The signed-in admin's own account in a right-hand drawer. The app shell opens it over any page
 * when the URL has `?profile=1` (top bar menu, Settings, or a /profile link).
 */
const ProfileSheet: React.FC<ProfileSheetProps> = ({ open, onOpenChange }) => {
  const profile = useProfileData();
  const content = React.useRef<HTMLDivElement | null>(null);
  const { user } = profile;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        ref={content}
        size="xl"
        // Focus the drawer itself, not its first button (the photo button would pop its tooltip,
        // and the first Escape would close that instead of the drawer).
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          content.current?.focus();
        }}
      >
        <SheetHeader
          title="Your profile"
          description="Your account, the WhatsApp number your sign-in codes go to, and your password."
        />
        <SheetBody>
          {user ? (
            <div className="divide-y divide-border">
              <div className="pb-6">
                <AccountSection profile={profile} />
              </div>
              <div className="py-6">
                <WhatsAppSection profile={profile} />
              </div>
              <div className="pt-6">
                <PasswordSection profile={profile} />
              </div>
            </div>
          ) : (
            <div className="space-y-6" aria-busy="true">
              <Skeleton className="h-40" />
              <Skeleton className="h-24" />
              <Skeleton className="h-16" />
            </div>
          )}
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
};

export default ProfileSheet;
